import React from 'react'
import { SourceInsights } from '../../../lib/types/SourceInsights'
import { formatCurrency, formatNumber } from '../../../lib/utils/formatNumber'
import classNames from 'classnames'
import PolarAreaChart from '../../../components/Charts/PolarAreaChart'
import BarChart from '../../Charts/BarChart'
import { HighlightCard } from '@demandlocal/components'

const SalesInsights = ({
  insights,
  chartTotal,
  isLoading,
  isPreviousData,
}: {
  chartTotal: number
  insights?: SourceInsights
  isLoading: boolean
  isPreviousData: boolean
}) => {
  return (
    <div className="flex flex-col gap-4">
      <span className="font-bold pt-10">Insights</span>
      <div className={classNames('grid grid-cols-2 gap-8')}>
        <HighlightCard
          isLoading={isLoading}
          isPrevious={isPreviousData}
          value={formatNumber(insights?.countByVinNull?.[0]?.count ?? 0)}
          content={<span>vehicle sales imported with a missing VIN</span>}
        />
        <HighlightCard
          isLoading={isLoading}
          isPrevious={isPreviousData}
          textSize="3xl"
          value={formatCurrency(insights?.averageCost?.[0]?.cost ?? 0)}
          content={<span>was the average sale price of the sold vehicles</span>}
        />
      </div>

      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Sold Vehicle States</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                insights?.vehicleStatus?.map((d) => ({
                  label: d.status,
                  value: d.items,
                })) ?? []
              }
              isLoading={isLoading}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Sold Vehicle Makes</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                insights?.vehicleMake?.slice(0, 5)?.map((d) => ({
                  label: d.make,
                  value: d.items,
                })) ?? []
              }
              isLoading={isLoading}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Sold Vehicle Years</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                insights?.vehicleYear?.slice(0, 5)?.map((d) => ({
                  label: d.year,
                  value: d.items,
                })) ?? []
              }
              isLoading={isLoading}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Sold Vehicles Cost Breakdown</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <BarChart
              data={
                insights?.costBreakdown?.map((d) => ({
                  label: d.cost,
                  value: d.items,
                })) ?? []
              }
              gradient={'green-to-lemon-green'}
              isLoading={isLoading}
              isPreviousData={isPreviousData}
              customOptions={{
                legend: {
                  display: false,
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesInsights
